import {CSSProperties} from "react";

// Brand and scheme colors as plain data, with no React context or app state, so pages that run
// outside the app (the invoice assistant extension, for one) can theme themselves.

export type OtsBrand = "corporate" | "field-go" | "water-go" | "oil-go" | "dispatch-go" | "hub-go";

export type OtsColorScheme = "light" | "dark";

/**
 * How a theme root picks light or dark.
 *
 * - `"inherit"` (default): whatever the enclosing theme root resolved to -- through portals too --
 *   else light. Keeps existing pages light until they opt in.
 * - `"system"`: the host page's theme, then the operating system's. The host theme is the nearest
 *   `data-theme` / `data-bs-theme` or `.dark` / `.light` ancestor, which is how WaterGo's GWT shell
 *   themes itself.
 * - `"light"` / `"dark"`: forced.
 */
export type OtsColorSchemePreference = OtsColorScheme | "system" | "inherit";

export const otsBrandPrimary: Record<OtsBrand, string> = {
  corporate: "#12284c",
  "field-go": "#027691",
  "water-go": "#253f91",
  "oil-go": "#111111",
  "dispatch-go": "#58595b",
  "hub-go": "#12284c"
};

type BrandColors = {primary: string; onPrimary: string; primaryContainer: string};

const brandColors: Record<OtsColorScheme, Record<OtsBrand, BrandColors>> = {
  light: {
    corporate: {primary: otsBrandPrimary.corporate, onPrimary: "#ffffff", primaryContainer: "#e5edf5"},
    "field-go": {primary: otsBrandPrimary["field-go"], onPrimary: "#ffffff", primaryContainer: "#d8f1f6"},
    "water-go": {primary: otsBrandPrimary["water-go"], onPrimary: "#ffffff", primaryContainer: "#e5edf5"},
    "oil-go": {primary: otsBrandPrimary["oil-go"], onPrimary: "#ffffff", primaryContainer: "#e5edf5"},
    "dispatch-go": {primary: otsBrandPrimary["dispatch-go"], onPrimary: "#ffffff", primaryContainer: "#e5edf5"},
    "hub-go": {primary: otsBrandPrimary["hub-go"], onPrimary: "#ffffff", primaryContainer: "#e5edf5"}
  },
  // Lighter tints of each brand: the light-mode primaries are too dark to read on a dark surface.
  dark: {
    corporate: {primary: "#9dbbe3", onPrimary: "#0b1626", primaryContainer: "#24406a"},
    "field-go": {primary: "#5fd0e6", onPrimary: "#04232b", primaryContainer: "#0e4552"},
    "water-go": {primary: "#a3b6f5", onPrimary: "#0d1638", primaryContainer: "#283c80"},
    "oil-go": {primary: "#e4e4e7", onPrimary: "#111111", primaryContainer: "#3a3a40"},
    "dispatch-go": {primary: "#c8cacd", onPrimary: "#1c1d1f", primaryContainer: "#3c3f44"},
    "hub-go": {primary: "#9dbbe3", onPrimary: "#0b1626", primaryContainer: "#24406a"}
  }
};

/*
 * Everything a component colors should come from these tokens rather than hex values or `dark:`
 * classes: `dark:` follows only the operating system, so it would disagree with a forced scheme or a
 * GWT shell that themes itself.
 */
const schemeColors: Record<OtsColorScheme, Record<`--ots-${string}`, string>> = {
  light: {
    "--ots-secondary": "#12284c",
    "--ots-accent": "#e1251b",
    "--ots-background": "#f7fafc",
    "--ots-surface": "#ffffff",
    "--ots-surface-variant": "#e5edf5",
    "--ots-field": "#ffffff",
    // Fields sit on a surface of the same white, so their edge has to carry them.
    "--ots-field-outline": "#b9c8d8",
    "--ots-text": "#0f172b",
    "--ots-text-muted": "#45556c",
    "--ots-outline": "#9db7d3",
    "--ots-outline-variant": "#d3dee9",
    "--ots-error": "#b91d15",
    "--ots-on-error": "#ffffff",
    "--ots-neutral-container": "#e5edf5",
    "--ots-on-neutral-container": "#45556c",
    "--ots-success": "#206c31",
    "--ots-success-container": "#e3f7e7",
    "--ots-on-success-container": "#1b4d26",
    "--ots-success-outline": "#b7e4c0",
    "--ots-warning": "#7a4700",
    "--ots-warning-container": "#fff3d6",
    "--ots-on-warning-container": "#5c3600",
    "--ots-warning-outline": "#f5d48a",
    "--ots-caution": "#d97706",
    // Dispatch service-work filled action: the legacy palette at its light/dark primary shade.
    "--ots-service-work": "#b33000",
    "--ots-on-service-work": "#ffffff",
    "--ots-danger-container": "#fff1f0",
    "--ots-on-danger-container": "#7e1d19",
    "--ots-danger-outline": "#ffc7c3",
    "--ots-blue": "#2563eb",
    "--ots-blue-container": "#eff6ff",
    "--ots-on-blue-container": "#1e3a8a",
    "--ots-blue-outline": "#bfdbfe",
    "--ots-yellow": "#6b5f00",
    "--ots-yellow-container": "#fffde6",
    "--ots-on-yellow-container": "#5c5200",
    "--ots-yellow-outline": "#fff000",
    "--ots-violet": "#8b3fa0",
    "--ots-violet-container": "#fbf0ff",
    "--ots-on-violet-container": "#652574",
    "--ots-violet-outline": "#e4b5ec",
    "--ots-indigo": "#4f46e5",
    "--ots-indigo-container": "#eef2ff",
    "--ots-on-indigo-container": "#3730a3",
    "--ots-indigo-outline": "#c7d2fe",
    "--ots-transport-category-water": "#22a7f0",
    "--ots-transport-category-oil": "#111111",
    "--ots-transport-category-oil-fill": "#475569",
    "--ots-load-water-near-capacity": "#0369a1",
    "--ots-load-oil": "#64748b",
    "--ots-load-oil-near-capacity": "#475569"
  },
  dark: {
    "--ots-secondary": "#f1f5fa",
    "--ots-accent": "#ff685f",
    "--ots-background": "#111c2d",
    "--ots-surface": "#1a293f",
    "--ots-surface-variant": "#253750",
    "--ots-field": "#132136",
    "--ots-field-outline": "#3b5374",
    "--ots-text": "#e3eaf3",
    "--ots-text-muted": "#a3b3c7",
    "--ots-outline": "#4f6787",
    "--ots-outline-variant": "#2e425e",
    "--ots-error": "#ff8a82",
    "--ots-on-error": "#3b0906",
    "--ots-neutral-container": "#2a3b54",
    "--ots-on-neutral-container": "#bccadb",
    "--ots-success": "#72d58e",
    "--ots-success-container": "#153a26",
    "--ots-on-success-container": "#c4ead0",
    "--ots-success-outline": "#26603b",
    "--ots-warning": "#f2c14e",
    "--ots-warning-container": "#3d2d0b",
    "--ots-on-warning-container": "#f6dea4",
    "--ots-warning-outline": "#6e5214",
    "--ots-caution": "#f0a73a",
    "--ots-service-work": "#4f1300",
    "--ots-on-service-work": "#ffffff",
    "--ots-danger-container": "#44181b",
    "--ots-on-danger-container": "#ffcbc6",
    "--ots-danger-outline": "#7c2c2c",
    "--ots-blue": "#93c5fd",
    "--ots-blue-container": "#172554",
    "--ots-on-blue-container": "#dbeafe",
    "--ots-blue-outline": "#1e40af",
    "--ots-yellow": "#fff58a",
    "--ots-yellow-container": "#3b3700",
    "--ots-on-yellow-container": "#fff8b8",
    "--ots-yellow-outline": "#c7bd00",
    "--ots-violet": "#dda4e8",
    "--ots-violet-container": "#42214a",
    "--ots-on-violet-container": "#f4d8f8",
    "--ots-violet-outline": "#703b7a",
    "--ots-indigo": "#a5b4fc",
    "--ots-indigo-container": "#27275d",
    "--ots-on-indigo-container": "#e0e7ff",
    "--ots-indigo-outline": "#44469b",
    "--ots-transport-category-water": "#5bc7f5",
    "--ots-transport-category-oil": "#e4e4e7",
    "--ots-transport-category-oil-fill": "#475569",
    "--ots-load-water-near-capacity": "#0ea5e9",
    "--ots-load-oil": "#64748b",
    "--ots-load-oil-near-capacity": "#475569"
  }
};

export type OtsThemeVariables = CSSProperties & Record<`--ots-${string}`, string>;

export const getOtsThemeVariables = (brand: OtsBrand, colorScheme: OtsColorScheme = "light"): OtsThemeVariables => {
  const brandColor = brandColors[colorScheme][brand];

  return {
    "--ots-primary": brandColor.primary,
    "--ots-on-primary": brandColor.onPrimary,
    "--ots-primary-container": brandColor.primaryContainer,
    ...schemeColors[colorScheme],
    "--ots-font-body": '"Inter Variable", "Inter", ui-sans-serif, system-ui, sans-serif',
    "--ots-font-display": '"Lexend Variable", "Lexend", "Inter Variable", "Inter", ui-sans-serif, system-ui, sans-serif'
  };
};
